import { Button, IconTrash, Stack, Text, Tooltip } from '@vtex/admin-ui'
import { BudgetPermissions, useBudgetContext } from 'hooks'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { type BaseBudget } from 'typings'
import { IconPause } from '../../../../../icons/IconPause'
import { ProgressBar } from '../../../../ProgressBar'
import { useNumbers } from '../../../../utils/useNumbers'
import { messages } from '../messages'
import { showTooltip } from '../shared/showTooltip'
import { styles } from '../styles'

type Props = {
  config: BaseBudget
  reason: 'discount-type' | 'budget-disabled'
  onRemove: () => void
}

export function BudgetDisabledView(props: Props) {
  const { config, reason, onRemove } = props
  const { $t } = useIntl()

  const { formatCurrency, formatPercentual } = useNumbers()

  const { amount, utilized } = config

  const percentage = useMemo(
    () => (amount ? (utilized / amount) * 100 : 0),
    [amount, utilized]
  )

  const { permission } = useBudgetContext()

  return (
    <Stack space="$space-3" fluid className={styles.hoverable}>
      <div className={styles.header}>
        <Text variant="title1">
          <FormattedMessage {...messages.title} />
        </Text>
        <div className={styles.actions}>
          <Tooltip
            text={$t(messages.unauthorizedWrite)}
            {...showTooltip(permission !== BudgetPermissions.WRITE)}
          >
            <div>
              <Button
                icon={<IconTrash />}
                variant="criticalTertiary"
                aria-label={$t(messages.delete)}
                onClick={onRemove}
                data-open={false}
                disabled={permission !== BudgetPermissions.WRITE}
              />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className={styles.content}>
        <Text variant="detail" tone="secondary">
          <FormattedMessage {...messages.totalSpent} />
        </Text>
        <Text className={styles.utilized} tone="secondary" variant="display">
          {formatCurrency(utilized)}
        </Text>
        <ProgressBar disabled percentage={percentage} />
        <div className={styles.legend}>
          <Text variant="detail" tone="secondary">
            {formatPercentual(percentage, 2)}
          </Text>
          <Text variant="body" tone="secondary">
            {formatCurrency(amount)}
          </Text>
        </div>
      </div>
      <div>
        <div data-testid="budget-stop-alert" className={styles.alertContainer}>
          <IconPause />
          <Text tone="secondary">
            <FormattedMessage
              {...(reason === 'discount-type'
                ? messages.disabled.discountType
                : messages.disabled.api)}
            />
          </Text>
        </div>
      </div>
    </Stack>
  )
}
